import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import rubikBase64 from '../fonts/rubik-font';
import { saveAs } from 'file-saver';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const AdminStats = () => {
  const { user: currentUser } = useAuth();
  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calOpen, setCalOpen] = useState(false);
  const [calYear, setCalYear] = useState(() => Number(month.split('-')[0]));
  const calRef = useRef(null);
  const [lockStatus, setLockStatus] = useState({}); // { userId: true/false }
  const [filterCompany, setFilterCompany] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard/stats', { params: { month } });
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch lock status for all users
  useEffect(() => {
    if (!isManager || !month) return;
    api.get('/attendance/locks', { params: { month } })
      .then(res => setLockStatus(res.data.data || {}))
      .catch(() => setLockStatus({}));
  }, [month, isManager]);

  const toggleLock = async (userId) => {
    const isLocked = lockStatus[userId];
    // Optimistic update
    setLockStatus(prev => ({ ...prev, [userId]: !isLocked }));
    try {
      if (isLocked) {
        await api.delete('/attendance/lock', { data: { month, user_id: userId } });
      } else {
        await api.post('/attendance/lock', { month, user_id: userId });
      }
    } catch (err) {
      // Revert
      setLockStatus(prev => ({ ...prev, [userId]: isLocked }));
      toast.error(err.response?.data?.message || 'Failed to update lock');
    }
  };

  if (!isManager) {
    return <div className="loading">Access denied</div>;
  }

  if (!stats) return <div className="loading">Loading...</div>;

  const { overview, monthly, users: allUsers } = stats;

  // Build filter options
  const companies = [...new Set(allUsers.map(u => u.company_name).filter(Boolean))].sort();
  const departments = [...new Set(allUsers.map(u => u.department_name).filter(Boolean))].sort();

  // Apply filters
  const users = allUsers.filter(u => {
    if (filterCompany && u.company_name !== filterCompany) return false;
    if (filterDepartment && u.department_name !== filterDepartment) return false;
    return true;
  });

  const getMonthLabel = () => {
    const [y, m] = month.split('-');
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getExportData = () => users.map(u => ({
    Name: u.name,
    Role: u.role,
    Present: u.present,
    'Sick Leave': u.sick,
    Vacation: u.vacation,
    'Total Hours': u.total_hours + 'h',
  }));

  const fixRTL = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/[\u0590-\u05FF\uFB1D-\uFB4F]+/g, (match) => match.split('').reverse().join(''))
      .replace(/^([\u0590-\u05FF\uFB1D-\uFB4F].*)$/gm, (line) => {
        const parts = line.split(/(\s+)/);
        return parts.reverse().join('');
      });
  };

  const exportPDF = () => {
    try {
      const data = getExportData();
      if (data.length === 0) { toast.error('No data to export'); return; }
      const doc = new jsPDF();
      doc.addFileToVFS('Rubik-Regular.ttf', rubikBase64);
      doc.addFont('Rubik-Regular.ttf', 'Rubik', 'normal');
      doc.setFont('Rubik');
      doc.setFontSize(16);
      doc.text(`Employee Stats - ${getMonthLabel()}`, 14, 18);
      autoTable(doc, {
        startY: 26,
        head: [['Name', 'Role', 'Present', 'Sick Leave', 'Vacation', 'Total Hours']],
        body: data.map(r => [fixRTL(r.Name), r.Role, r.Present, r['Sick Leave'], r.Vacation, r['Total Hours']]),
        styles: { fontSize: 9, cellPadding: 3, font: 'Rubik' },
        headStyles: { fillColor: [71, 182, 227], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      doc.save(`Employee_Stats_${month}.pdf`);
      toast.success('PDF exported');
    } catch (err) {
      toast.error('PDF export failed');
    }
  };

  const exportExcel = () => {
    const data = getExportData();
    if (data.length === 0) { toast.error('No data to export'); return; }
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Stats');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Employee_Stats_${month}.xlsx`);
    toast.success('Excel exported');
  };

  return (
    <div className={`admin-page${loading ? ' stats-loading' : ''}`}>
      <div className="page-header">
        <h1>{currentUser?.role === 'manager' ? 'Team Statistics' : 'Statistics'}</h1>
        <div className="month-nav">
          <button className="btn-month-arrow" onClick={() => {
            const [y, m] = month.split('-').map(Number);
            const d = new Date(y, m - 2, 1);
            if (d >= new Date(2022, 0, 1)) setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }} title="Previous month" disabled={month === '2022-01'}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="cal-dropdown" ref={calRef}>
            <button className="month-picker" onClick={() => { setCalYear(Number(month.split('-')[0])); setCalOpen(!calOpen); }}>
              {new Date(Number(month.split('-')[0]), Number(month.split('-')[1]) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '0.5rem' }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
            {calOpen && (
              <div className="cal-popup">
                <div className="cal-year-nav">
                  <button onClick={() => { if (calYear > 2022) setCalYear(calYear - 1); }} disabled={calYear <= 2022}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  <span className="cal-year-label">{calYear}</span>
                  <button onClick={() => { if (calYear < new Date().getFullYear()) setCalYear(calYear + 1); }} disabled={calYear >= new Date().getFullYear()}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
                <div className="cal-months">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((name, i) => {
                    const m = i + 1;
                    const now = new Date();
                    const isFuture = calYear === now.getFullYear() && m > now.getMonth() + 1;
                    const isSelected = calYear === Number(month.split('-')[0]) && m === Number(month.split('-')[1]);
                    return (
                      <button
                        key={m}
                        className={`cal-month-btn${isSelected ? ' selected' : ''}${isFuture ? ' disabled' : ''}`}
                        disabled={isFuture}
                        onClick={() => {
                          setMonth(`${calYear}-${String(m).padStart(2, '0')}`);
                          setCalOpen(false);
                        }}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                <div className="cal-footer">
                  <button className="cal-footer-btn" onClick={() => { setMonth(''); setCalOpen(false); setTimeout(() => { const now = new Date(); setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`); }, 0); }}>
                    This month
                  </button>
                </div>
              </div>
            )}
          </div>
          <button className="btn-month-arrow" onClick={() => {
            const [y, m] = month.split('-').map(Number);
            const d = new Date(y, m, 1);
            const now = new Date();
            const current = new Date(now.getFullYear(), now.getMonth(), 1);
            if (d <= current) setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }} title="Next month" disabled={(() => {
            const [y, m] = month.split('-').map(Number);
            const next = new Date(y, m, 1);
            const now = new Date();
            const current = new Date(now.getFullYear(), now.getMonth(), 1);
            return next > current;
          })()}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="stats-filters">
        <div className="filter-group">
          <label>Company</label>
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Department</label>
          <select value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* Today's Overview */}
      <div className="section-title">Today</div>
      <div className="overview-cards">
        <div className="overview-card clickable" onClick={() => navigate('/admin/users')}>
          <div className="card-icon blue">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{overview.total_employees}</span>
            <span className="card-desc">Total Employees</span>
          </div>
        </div>
        <div className="overview-card clickable" onClick={() => navigate('/admin/users?status=present')}>
          <div className="card-icon green">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{overview.present_today}</span>
            <span className="card-desc">Present Today</span>
          </div>
        </div>
        <div className="overview-card clickable" onClick={() => navigate('/admin/users?status=clocked_in')}>
          <div className="card-icon green">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M20 21a8 8 0 0 0-16 0" />
              <circle cx="12" cy="8" r="1" fill="currentColor" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{overview.clocked_in_now || 0}</span>
            <span className="card-desc">Currently Working</span>
          </div>
        </div>
        <div className="overview-card clickable" onClick={() => navigate('/admin/users?status=sick')}>
          <div className="card-icon red">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{overview.sick_today || 0}</span>
            <span className="card-desc">Sick Today</span>
          </div>
        </div>
        <div className="overview-card clickable" onClick={() => navigate('/admin/users?status=vacation')}>
          <div className="card-icon yellow">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{overview.vacation_today}</span>
            <span className="card-desc">Vacation</span>
          </div>
        </div>
        <div className="overview-card clickable" onClick={() => navigate('/admin/users?status=missing')}>
          <div className="card-icon purple">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{Math.max(0, (overview.total_employees || 0) - (overview.present_today || 0) - (overview.sick_today || 0) - (overview.vacation_today || 0))}</span>
            <span className="card-desc">Missing Status</span>
          </div>
        </div>
      </div>

      {/* Monthly Overview */}
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Monthly Summary
        {currentUser?.role === 'admin' && (
          <button className="btn-export btn-export-excel" onClick={() => navigate('/admin/stats-daily')} title="Open Statistics Daily">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Statistics Daily
          </button>
        )}
      </div>
      <div className="overview-cards">
        <div className="overview-card">
          <div className="card-icon green">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="card-data">
            <span className="card-value">{monthly.avg_attendance}%</span>
            <span className="card-desc">Avg Attendance</span>
          </div>
        </div>
        <div className="overview-card">
          <div className="card-icon green">
            <div className="card-number-only">{monthly.total_present}</div>
          </div>
          <div className="card-data">
            <span className="card-desc">Total Present</span>
          </div>
        </div>
        <div className="overview-card">
          <div className="card-icon red">
            <div className="card-number-only">{monthly.total_sick}</div>
          </div>
          <div className="card-data">
            <span className="card-desc">Total Sick Leave</span>
          </div>
        </div>
        <div className="overview-card">
          <div className="card-icon yellow">
            <div className="card-number-only">{monthly.total_vacation}</div>
          </div>
          <div className="card-data">
            <span className="card-desc">Total Vacation</span>
          </div>
        </div>
      </div>

      {/* Per User Stats */}
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Per Employee
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-export btn-export-pdf" onClick={exportPDF} title="Export as PDF">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            Export PDF
          </button>
          <button className="btn-export btn-export-excel" onClick={exportExcel} title="Export as Excel">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            Export Excel
          </button>
        </div>
      </div>
      {(() => {
        const renderUserRow = (u) => (
          <tr key={u.id}>
            <td><strong className="clickable-name" onClick={() => navigate(`/attendance?user_id=${u.id}&month=${month}`)}>{u.name}</strong></td>
            <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
            <td className="stat-green">{u.present}</td>
            <td className="stat-red">{u.sick}</td>
            <td className="stat-blue">{u.vacation}</td>
            <td><strong>{u.total_hours}h</strong></td>
            {isManager && (
              <td style={{ textAlign: 'center' }}>
                <button
                  className={`btn-lock-icon ${lockStatus[u.id] ? 'locked' : ''}`}
                  onClick={() => toggleLock(u.id)}
                  title={lockStatus[u.id] ? 'Unlock timesheet' : 'Lock timesheet'}
                >
                  {lockStatus[u.id] ? (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 4.83 3.67" />
                    </svg>
                  )}
                </button>
              </td>
            )}
          </tr>
        );

        const tableHead = (
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Present</th>
              <th>Sick Leave</th>
              <th>Vacation</th>
              <th>Total Hours</th>
              {isManager && <th style={{ width: '70px', textAlign: 'center' }}>Lock</th>}
            </tr>
          </thead>
        );

        // Group by department
        const deptMap = {};
        for (const u of users) {
          const key = u.department_name || 'No Department';
          if (!deptMap[key]) deptMap[key] = [];
          deptMap[key].push(u);
        }
        const deptNames = Object.keys(deptMap).sort((a, b) => a === 'No Department' ? 1 : b === 'No Department' ? -1 : a.localeCompare(b));

        if (deptNames.length <= 1) {
          return (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                {tableHead}
                <tbody>{users.map(renderUserRow)}</tbody>
              </table>
            </div>
          );
        }

        return deptNames.map(deptName => (
          <div key={deptName} className="dept-group">
            <h2 className="dept-group-title">{deptName} <span className="dept-group-count">({deptMap[deptName].length})</span></h2>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                {tableHead}
                <tbody>{deptMap[deptName].map(renderUserRow)}</tbody>
              </table>
            </div>
          </div>
        ));
      })()}
    </div>
  );
};

export default AdminStats;
